<template>
  <BaseNavigationBar title="Редактирование шаблона" />

  <BaseIsland size="lg">
    <form class="three-template-edit" @submit.prevent="handleSave">
      <!-- Header: Название и описание проекта -->
      <div class="three-template-edit__header">
        <BaseForm
          ref="formRef1"
          v-model="formModel"
          :schema="threeTemplateHeaderSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.header"
          @add="handleStartAddButton('header')"
          @edit="handleOpenModuleSelect('header')"
          @clear="handleClearButton('header')"
        />
      </div>

      <!-- Основная секция: изображение слева и изображение справа -->
      <div class="three-template-edit__main-section">
        <div class="three-template-edit__main-image">
          <TemplateMediaSlot
            :model-value="images[0]"
            @update:model-value="(urls) => handleUpdateImage(0, urls)"
          />
        </div>
        <div class="three-template-edit__side-image">
          <TemplateMediaSlot
            :model-value="images[1]"
            @update:model-value="(urls) => handleUpdateImage(1, urls)"
          />
        </div>
      </div>

      <!-- Три заголовка H2 -->
      <div class="three-template-edit__triple-headers">
        <div class="three-template-edit__header-item">
          <BaseForm
            ref="formRef2"
            v-model="formModel"
            :schema="threeTemplateLeftHeaderSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.leftHeader"
            @add="handleStartAddButton('leftHeader')"
            @edit="handleOpenModuleSelect('leftHeader')"
            @clear="handleClearButton('leftHeader')"
          />
        </div>
        <div class="three-template-edit__header-item">
          <BaseForm
            ref="formRef3"
            v-model="formModel"
            :schema="threeTemplateCenterHeaderSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.centerHeader"
            @add="handleStartAddButton('centerHeader')"
            @edit="handleOpenModuleSelect('centerHeader')"
            @clear="handleClearButton('centerHeader')"
          />
        </div>
        <div class="three-template-edit__header-item">
          <BaseForm
            ref="formRef4"
            v-model="formModel"
            :schema="threeTemplateRightHeaderSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
          <TemplateLandingButtonSlot
            full-width
            :button="buttons.rightHeader"
            @add="handleStartAddButton('rightHeader')"
            @edit="handleOpenModuleSelect('rightHeader')"
            @clear="handleClearButton('rightHeader')"
          />
        </div>
      </div>

      <!-- Секция с текстом слева и изображением справа -->
      <div class="three-template-edit__text-image-section">
        <div class="three-template-edit__text-content">
          <BaseForm
            ref="formRef5"
            v-model="formModel"
            :schema="threeTemplateTextImageSectionSchema"
            :with-form-wrapper="false"
            :with-error-message="false"
          />
        </div>
        <div class="three-template-edit__text-image">
          <TemplateMediaSlot
            :model-value="images[2]"
            @update:model-value="(urls) => handleUpdateImage(2, urls)"
          />
        </div>
      </div>

      <!-- Дополнительный блок с заголовком и описанием -->
      <div class="three-template-edit__additional-section">
        <BaseForm
          ref="formRef6"
          v-model="formModel"
          :schema="threeTemplateAdditionalSectionSchema"
          :with-form-wrapper="false"
          :with-error-message="false"
        />
        <TemplateLandingButtonSlot
          :button="buttons.additional"
          @add="handleStartAddButton('additional')"
          @edit="handleOpenModuleSelect('additional')"
          @clear="handleClearButton('additional')"
        />
      </div>

      <BaseButton
        variant="primary"
        type="submit"
        max-width="335px"
        with-full
        with-decor
        :loading="loading.value"
        :disabled="loading.value"
      >
        Сохранить
      </BaseButton>
    </form>
  </BaseIsland>

  <LandingButtonTypePopup
    v-model="isButtonTypePopupOpen"
    @select-module="handleChooseButtonType('module')"
    @select-object="handleChooseButtonType('moduleObject')"
  />
</template>

<script setup lang="ts">
import type { IThreeFormData, IThreeTemplateData } from '~/entities/projects/model/templates-interface'
import type { IFormExpose } from '~/shared/ui'
import { ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useTemplateEditProjectOrRequestMeta } from '~/entities/projects'
import { templateId } from '~/entities/projects/model/const'
import { navigateAfterTemplateSave } from '~/entities/projects/model/navigateAfterTemplateSave'
import { validateTemplateImages } from '~/entities/projects/model/templateImageValidation'
import {
  threeTemplateAdditionalSectionSchema,
  threeTemplateCenterHeaderSchema,
  threeTemplateHeaderSchema,
  threeTemplateLeftHeaderSchema,
  threeTemplateRightHeaderSchema,
  threeTemplateTextImageSectionSchema,
} from '~/entities/projects/model/templates-schema'
import { uploadProjectImage } from '~/entities/projects/model/uploadProjectImage'
import { useTemplateEditButtons } from '~/entities/projects/model/useTemplateEditButtons'
import { useTemplateManager } from '~/entities/projects/model/useTemplateManager'
import LandingButtonTypePopup from '~/entities/projects/ui/templates/LandingButtonTypePopup.vue'
import TemplateLandingButtonSlot from '~/entities/projects/ui/templates/TemplateLandingButtonSlot.vue'
import TemplateMediaSlot from '~/entities/projects/ui/templates/TemplateMediaSlot.vue'
import { permissions, useLoading } from '~/shared/lib'
import {
  BaseButton,
  BaseForm,
  BaseIsland,
  BaseNavigationBar,
} from '~/shared/ui'

const router = useRouter()
const route = useRoute()

const { uploadFromBlobUrl } = uploadProjectImage()
const { isProjectRequestContext, resolvedProjectTitle, projectId, routeParamForLinks } = useTemplateEditProjectOrRequestMeta()
const { hasExistingTemplate, existingTemplate, saveTemplate } = useTemplateManager(() =>
  isProjectRequestContext.value
    ? { projectRequestId: route.params.id as string }
    : { projectId: projectId.value },
)

const images = ref<string[]>([])
const loading = useLoading()

const formModel = ref<IThreeFormData>({
  title: '',
  description: '',
  leftSectionTitle: '',
  leftSectionDescription: '',
  centerSectionTitle: '',
  centerSectionDescription: '',
  rightSectionTitle: '',
  rightSectionDescription: '',
  textImageSectionTitle: '',
  textImageSectionDescription: '',
  bottomSectionTitle: '',
  bottomSectionDescription: '',
})

const {
  buttons,
  isButtonTypePopupOpen,
  loadButtonsFromContent,
  handleStartAddButton,
  handleOpenModuleSelect,
  handleChooseButtonType,
  handleClearButton,
  getButtonsForSave,
  clearDraft,
} = useTemplateEditButtons({
  templateNumericId: templateId.three_template,
  editRoutePermission: permissions.PROJECTS.WORKSPACE.ABOUT.TEMPLATES.THREE_TEMPLATE.EDIT.PERMISSION,
  getDraftPayload: () => ({
    formModel: { ...formModel.value },
    images: [...images.value],
    buttons: { ...buttons.value },
  }),
  applyDraftPayload: (draft) => {
    formModel.value = draft.formModel as IThreeFormData
    images.value = [...draft.images]
    buttons.value = { ...draft.buttons }
  },
})

const formRef1 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef2 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef3 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef4 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef5 = ref<ComponentPublicInstance<IFormExpose>>()
const formRef6 = ref<ComponentPublicInstance<IFormExpose>>()

watch(existingTemplate, (template) => {
  if (template?.content && hasExistingTemplate.value) {
    const content = template.content as unknown as IThreeTemplateData

    if (content.id === templateId.three_template) {
      formModel.value = {
        title: content.title || resolvedProjectTitle.value,
        description: content.description || '',
        leftSectionTitle: content.leftSectionTitle || '',
        leftSectionDescription: content.leftSectionDescription || '',
        centerSectionTitle: content.centerSectionTitle || '',
        centerSectionDescription: content.centerSectionDescription || '',
        rightSectionTitle: content.rightSectionTitle || '',
        rightSectionDescription: content.rightSectionDescription || '',
        textImageSectionTitle: content.textImageSectionTitle || '',
        textImageSectionDescription: content.textImageSectionDescription || '',
        bottomSectionTitle: content.bottomSectionTitle || '',
        bottomSectionDescription: content.bottomSectionDescription || '',
      }

      if (content.images) {
        images.value = [
          content.images.leftMain || '',
          content.images.rightMain || '',
          content.images.textImage || '',
        ]
      }

      loadButtonsFromContent(content.buttons)
    }
  }
}, { immediate: true })

watch(
  resolvedProjectTitle,
  (name) => {
    if (name && !formModel.value.title) {
      formModel.value.title = name
    }
  },
  { immediate: true },
)

function handleUpdateImage(index: number, urls: string[]) {
  const url = urls[0] ?? ''
  const next = [...images.value]
  while (next.length <= index) {
    next.push('')
  }
  next[index] = url
  images.value = next
}

async function handleSave() {
  const validationResults = await Promise.all([
    formRef1.value?.validate(),
    formRef2.value?.validate(),
    formRef3.value?.validate(),
    formRef4.value?.validate(),
    formRef5.value?.validate(),
    formRef6.value?.validate(),
  ])

  const allValid = validationResults.every(result => result?.valid)

  if (!allValid) {
    return
  }

  const uploadPromises = images.value.map(url => url ? uploadFromBlobUrl(url) : Promise.resolve(null))
  const uploadedUrls = await Promise.all(uploadPromises)

  const finalImages = images.value.map((image, index) =>
    uploadedUrls[index] || image,
  )

  if (!validateTemplateImages({
    requiredImageCount: 3,
    images: finalImages,
  })) {
    return
  }

  await loading.wrapLoading(async () => {
    const templateContent = {
      id: templateId.three_template,
      title: resolvedProjectTitle.value,
      description: formModel.value.description,
      leftSectionTitle: formModel.value.leftSectionTitle,
      leftSectionDescription: formModel.value.leftSectionDescription,
      centerSectionTitle: formModel.value.centerSectionTitle,
      centerSectionDescription: formModel.value.centerSectionDescription,
      rightSectionTitle: formModel.value.rightSectionTitle,
      rightSectionDescription: formModel.value.rightSectionDescription,
      textImageSectionTitle: formModel.value.textImageSectionTitle,
      textImageSectionDescription: formModel.value.textImageSectionDescription,
      bottomSectionTitle: formModel.value.bottomSectionTitle,
      bottomSectionDescription: formModel.value.bottomSectionDescription,
      buttons: getButtonsForSave(),
      images: {
        leftMain: finalImages[0],
        rightMain: finalImages[1],
        textImage: finalImages[2],
      },
    }

    await saveTemplate(templateContent as IThreeTemplateData)
    clearDraft()

    await navigateAfterTemplateSave(
      router,
      route.query,
      isProjectRequestContext.value
        ? (route.params.id as string)
        : routeParamForLinks.value,
    )
  })
}
</script>

<style lang="scss" scoped>
.three-template-edit {
  display: grid;
  gap: rem(48px);
  grid-template-columns: 1fr;

  &__header {
    display: grid;
    gap: rem(16px);
  }

  &__main-section {
    display: grid;
    grid-template-columns: 2.3fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }

    @include bp-media('md', 'lg') {
      gap: rem(16px);
    }
  }

  &__main-image {
    overflow: auto;
    border-radius: rem(16px);
    display: grid;
    width: 100%;
  }

  &__side-image {
    overflow: auto;
    border-radius: rem(16px);
    display: grid;
    width: 100%;
  }

  &__triple-headers {
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm') {
      grid-template-columns: 1fr;
      gap: rem(24px);
    }

    @include bp-media('md') {
      gap: rem(16px);

      :deep(.base-input) {
        max-width: 240px;
      }

      :deep(.base-textarea) {
        max-width: 240px;
      }
    }
  }

  &__header-item {
    display: grid;
    gap: rem(16px);

    @include bp-media('md', 'lg') {
      max-width: 240px;
    }
  }

  &__text-image-section {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: rem(32px);

    @include bp-media('xs', 'sm', 'md', 'lg') {
      grid-template-columns: 1fr;
      gap: rem(24px);

      :deep(.template-media-slot) {
        overflow: auto;
        width: 690px;
      }
    }
  }

  &__text-content {
    display: flex;
    flex-direction: column;
    gap: rem(8px);
  }

  &__text-image {
    width: 100%;

    :deep(.template-media-slot) {
      @include bp-media('md', 'lg') {
        width: 100%;
      }

      @include bp-media('sm') {
        width: 100%;
      }

      @include bp-media('xs', 'sm') {
        width: 100%;
      }
    }
  }

  &__additional-section {
    display: grid;
    gap: rem(16px);
  }
}
</style>
